/**
 * AidCardSection - Homepage section showcasing the ΛiD Card feature
 *
 * Displays the Apple Wallet integration preview with QRG handshake capabilities.
 * Uses pluggable hooks for wallet and QRG operations to allow backend integration.
 *
 * @example
 * <AidCardSection
 *   designPreview={true}
 *   onAddToWallet={async () => { await api.createWalletPass() }}
 *   onRequestQRG={async () => { const { id } = await api.createAuthRequest(); return id }}
 * />
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { GlassCard, Button } from '@/components/ui'
import { useLocale } from '../hooks/useLocale'
import { ShieldCheck, CreditCard, QrCode, Wallet, Smartphone, X, EyeOff } from 'lucide-react'

type AidCardSectionProps = {
  /**
   * Called when user clicks "Add to Wallet".
   * Should return void or a Promise (for UI loading).
   */
  onAddToWallet?: () => Promise<void> | void

  /**
   * Called when user clicks "Show QRG".
   * Should return a URL (string) for the QR or an authRequestId.
   */
  onRequestQRG?: () => Promise<string> | string

  /**
   * If true, show the "In design" badge and keep CTA disabled.
   * Default: true (preview mode)
   */
  designPreview?: boolean
}

export default function AidCardSection({
  onAddToWallet,
  onRequestQRG,
  designPreview = true,
}: AidCardSectionProps) {
  const { t, tNested, tDeep } = useLocale()
  const [loadingWallet, setLoadingWallet] = useState(false)
  const [loadingQRG, setLoadingQRG] = useState(false)
  const [qrgUrl, setQrgUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Feature flag from environment (optional)
  const AID_CARD_ENABLED = import.meta.env.VITE_AID_CARD_ENABLED === 'true'

  async function handleAddToWallet() {
    setError(null)
    if (!onAddToWallet) return
    try {
      setLoadingWallet(true)
      await onAddToWallet()
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to add card'
      setError(errorMessage)
    } finally {
      setLoadingWallet(false)
    }
  }

  async function handleShowQRG() {
    setError(null)
    if (!onRequestQRG) return
    try {
      setLoadingQRG(true)
      const urlOrId = await onRequestQRG()
      // Accept full URL or just an ID
      const url = urlOrId.startsWith('http') ? urlOrId : `https://lukhas.id/qrg/${urlOrId}`
      setQrgUrl(url)
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to create QRG'
      setError(errorMessage)
    } finally {
      setLoadingQRG(false)
    }
  }

  const isDisabled = !AID_CARD_ENABLED || designPreview

  return (
    <section
      className="py-24 px-6 relative z-10 overflow-hidden"
      aria-labelledby="aid-card-heading"
    >
      {/* Background gradient — teal to contrast with indigo particle canvas */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-teal-950/10 to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        {/* Section Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-white/30 mb-4 block">
            {t('aidCard', 'sectionLabel')}
          </span>

          <h2 id="aid-card-heading" className="text-4xl md:text-5xl font-extralight text-white mb-6">
            <span className="block">{t('aidCard', 'title1')}</span>
            <span className="block text-white/40">{t('aidCard', 'title2')}</span>
          </h2>

          <p className="text-lg text-white/70 max-w-2xl mx-auto font-light leading-relaxed">
            {t('aidCard', 'description')}
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left: Card Visual */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full lg:w-1/2 flex justify-center"
          >
            <div className="relative">
              {/* Ambient glow — teal/cyan to pop against indigo background */}
              <div
                className="absolute -inset-10 rounded-full blur-[80px] opacity-30"
                style={{ background: 'radial-gradient(circle, #2dd4bf 0%, #0d9488 50%, transparent 100%)' }}
              />

              <GlassCard className="relative w-[340px] p-6">
                <div className="flex flex-col gap-6">
                  {/* Card Header */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                      <span className="text-2xl font-extralight text-white">Λ</span>
                    </div>
                    <div>
                      <div className="text-xs text-white/50 uppercase tracking-widest">ΛiD Card</div>
                      <div className="text-lg text-white font-light">ΛID · •••• 5678</div>
                    </div>
                  </div>

                  {/* CTA: create ΛiD */}
                  <div className="text-xs text-white/50 mt-2">
                    <a
                      href="https://lukhas.id/auth/onboarding"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-teal-300 hover:text-teal-200 transition-colors"
                    >
                      Create your ΛiD at lukhas.id
                    </a>
                  </div>

                  {/* Status Card */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="text-[10px] text-white/40 uppercase tracking-widest mb-3">Status</div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-emerald-400" />
                        <div>
                          <div className="text-sm font-medium text-white">Verified</div>
                          <div className="text-xs text-white/50">Passkeys enabled</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[10px] text-white/40 uppercase tracking-wider">QRG Ready</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      size="md"
                      className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                      onClick={handleAddToWallet}
                      disabled={isDisabled || !onAddToWallet || loadingWallet}
                      aria-disabled={isDisabled}
                      aria-label={designPreview ? 'Add to Wallet (preview)' : 'Add your ΛiD Card to Apple Wallet'}
                    >
                      {loadingWallet ? (
                        <span className="flex items-center gap-2" role="status" aria-live="polite">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Adding...
                        </span>
                      ) : (
                        <>
                          <Wallet className="w-4 h-4 mr-2" aria-hidden="true" />
                          {designPreview ? 'Coming Q2 2026' : 'Add to Wallet'}
                        </>
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      size="md"
                      className="flex-1"
                      onClick={handleShowQRG}
                      disabled={!onRequestQRG || loadingQRG}
                    >
                      {loadingQRG ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        </span>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Show QRG
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Privacy microcopy */}
                  <p className="text-xs text-white/40 mt-3">
                    <strong className="text-white/50">Privacy-first.</strong> Your ΛiD Card shares minimal data. Use selective disclosure (ZKP) to prove attributes without revealing personal data.
                  </p>
                </div>
              </GlassCard>

              {/* "In design" badge */}
              {designPreview && (
                <div className="absolute -top-3 -right-3 bg-white/10 backdrop-blur-sm text-white/80 text-[10px] px-3 py-1.5 rounded-full border border-white/20 uppercase tracking-widest">
                  {t('aidCard', 'badge')}
                </div>
              )}
            </div>
          </motion.div>

          {/* Right: Features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-full lg:w-1/2"
          >
            <div className="space-y-8">
              {/* Feature 1: Apple Wallet */}
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    {tDeep('aidCard', 'features', 'wallet', 'label')}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {tDeep('aidCard', 'features', 'wallet', 'value')}
                  </p>
                </div>
              </div>

              {/* Feature 2: QRG Handshakes */}
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    {tDeep('aidCard', 'features', 'qrg', 'label')}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {tDeep('aidCard', 'features', 'qrg', 'value')}
                  </p>
                </div>
              </div>

              {/* Feature 3: Session Approval */}
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    {tDeep('aidCard', 'features', 'sso', 'label')}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {tDeep('aidCard', 'features', 'sso', 'value')}
                  </p>
                </div>
              </div>

              {/* Feature 4: Selective Sharing */}
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    {tDeep('aidCard', 'features', 'selective', 'label')}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {tDeep('aidCard', 'features', 'selective', 'value')}
                  </p>
                </div>
              </div>

              {/* Error display */}
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* QRG Display with QR Code */}
              <AnimatePresence mode="wait">
                {qrgUrl && !loadingQRG && (
                  <motion.div
                    key="qrg-display"
                    initial={{ opacity: 0, y: 30, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{
                      duration: 0.5,
                      ease: "easeOut",
                      opacity: { duration: 0.3 }
                    }}
                    className="p-6 bg-white/5 border border-white/10 rounded-xl"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="text-xs text-white/40 uppercase tracking-widest mb-1">QRG Handshake</div>
                        <div className="text-sm text-white/70">Scan to authenticate</div>
                      </div>
                      <button
                        onClick={() => setQrgUrl(null)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                        aria-label="Close QRG"
                      >
                        <X className="w-4 h-4 text-white/40" />
                      </button>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      {/* QR Code with glow */}
                      <div className="relative">
                        {/* Glow effect behind QR */}
                        <div className="absolute -inset-4 bg-teal-500/30 rounded-2xl blur-xl animate-pulse" />
                        <div className="relative bg-white p-3 rounded-xl shadow-lg">
                          <QRCodeSVG
                            value={qrgUrl}
                            size={140}
                            level="M"
                            includeMargin={false}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                            aria-label="ΛiD QR handshake code"
                          />
                        </div>
                      </div>

                      {/* QRG Info */}
                      <div className="flex-1 space-y-3">
                        {/* Status indicator */}
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-sm text-emerald-400 font-medium">Active</span>
                          <span className="text-xs text-white/40">• Expires in 30s</span>
                        </div>

                        {/* URL display */}
                        <div className="text-xs text-white/40 break-all font-mono bg-white/5 p-2 rounded">
                          {qrgUrl}
                        </div>

                        {/* Link */}
                        <a
                          href={qrgUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-teal-400 hover:text-purple-300 transition-colors"
                        >
                          <QrCode className="w-4 h-4" />
                          Open in new tab
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Coming soon note */}
              <p className="text-xs text-white/30 italic">
                {t('aidCard', 'comingSoon')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
